import type {QueryClient} from "@tanstack/react-query";

import {NOTIFICATION_FEED_QUERY_KEY} from "../hooks/use-notification-feed";
import {notificationFeedService} from "../services";
import {useNotificationFeedStore} from "../store";
import type {NotificationItem} from "../types";

export function getDirectMessageThreadId(item: NotificationItem): number | null {
  if (item.topic !== "direct_message") {
    return null;
  }
  if (typeof item.payload.direct_message_thread_id === "number") {
    return item.payload.direct_message_thread_id;
  }
  if (typeof item.subject.id === "number") {
    return item.subject.id;
  }
  return null;
}

export function getUnreadDirectMessageNotificationIdsForThread(threadId: number): string[] {
  const {items} = useNotificationFeedStore.getState();

  return items
    .filter((item) => {
      if (item.topic !== "direct_message" || item.read_at || item.discarded_at) {
        return false;
      }
      return getDirectMessageThreadId(item) === threadId;
    })
    .map((item) => item.id);
}

/** Optimistic + API mark-read for every unread DM feed row on this thread. GET thread also acks server-side. */
export async function ackDirectMessageNotificationsForThread(
  threadId: number,
  queryClient?: QueryClient,
): Promise<void> {
  const ids = getUnreadDirectMessageNotificationIdsForThread(threadId);
  if (!ids.length) {
    return;
  }

  useNotificationFeedStore.getState().markReadLocal(ids);

  try {
    await notificationFeedService.markReadByIds(ids);
  } catch {
    // Opening the thread (GET) remains the domain ack if mark-read fails.
  }

  if (queryClient) {
    void queryClient.invalidateQueries({queryKey: [NOTIFICATION_FEED_QUERY_KEY]});
  }
}

export function ackDirectMessageNotificationItem(
  item: NotificationItem,
  queryClient?: QueryClient,
): Promise<void> {
  const threadId = getDirectMessageThreadId(item);
  if (threadId == null) {
    return Promise.resolve();
  }
  return ackDirectMessageNotificationsForThread(threadId, queryClient);
}
